import React, { useState } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import MobileBottomBar from './MobileBottomBar';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Outlet } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';
import OmniActionFab from './OmniActionFab';

interface LayoutProps {
  children?: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileSidebarOpen(!mobileSidebarOpen);
    } else {
      setSidebarOpen(!sidebarOpen);
    }
  };

  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') return '';
    if (path === '/profile') return '';
    if (path === '/customers') return t('settings.customer_management');
    if (path.includes('/customers/') && path.includes('/edit')) return t('settings.edit_customer');
    if (path.includes('/customers/') && path.includes('/new')) return t('settings.new_customer');
    if (path.includes('/customers/')) return t('settings.customer_detail');
    if (path === '/events') return t('settings.event_management');
    if (path.includes('/events/') && path.includes('/edit')) return t('settings.edit_event');
    if (path.includes('/events/') && path.includes('/new')) return t('settings.new_event');
    if (path.includes('/events/')) return t('settings.event_detail');
    if (path === '/payments') return t('nav.finances');
    if (path === '/settings') return '';
    if (path === '/inventory') return '';
    if (path === '/sales') return '';
    return 'NEXUS';
  };

  const pageTitle = getPageTitle();

  return (
    <div className="min-h-screen bg-background flex w-full">
      <Sidebar
        isOpen={isMobile ? mobileSidebarOpen : sidebarOpen}
        onClose={() => (isMobile ? setMobileSidebarOpen(false) : setSidebarOpen(false))}
      />

      <div
        className={cn(
          'flex-1 flex flex-col min-h-screen w-full transition-all duration-300 ease-in-out',
          !isMobile && sidebarOpen ? 'lg:ml-64' : !isMobile ? 'lg:ml-16' : ''
        )}
      >
        <div className="sticky top-0 z-40 bg-card border-b border-border shadow-sm w-full">
          <Header toggleSidebar={toggleSidebar} />
        </div>

        <main className="flex-1 p-4 pb-28 lg:p-8 lg:pb-8 w-full max-w-[1600px] mx-auto">
          {pageTitle && (
            <div className="mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">{pageTitle}</h1>
            </div>
          )}
          {children || <Outlet />}
        </main>

        <MobileBottomBar onToggleMenu={toggleSidebar} />
        <OmniActionFab />
      </div>
    </div>
  );
};

export default Layout;
